import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import { MdKeyboardArrowRight } from 'react-icons/md';

import api from '../../services/api';

import Header from '../../containers/Header';
import Footer from '../../containers/Footer';
import Breadcrumbs from '../../containers/Breadcrumbs';

import Card from '../../components/Card';
import Datatable from '../../components/Datatable';
import Button from '../../components/Button';
import DropdownButton from '../../components/DropdownButton';
import Badge from '../../components/Badge';
import formatDate from '../../utils/formatDate';
import Checkbox from '../../components/Checkbox';

import { FilterCard, CheckboxGroup } from './styles';

interface Accused {
  id: string;
  show_name: string;
  role: string;
}

interface Category {
  id: string;
  title: string;
}

interface StatusInfo {
  name: string;
  color: 'warning' | 'success' | 'error';
}

interface ReportData {
  id: string;
  category: Category;
  accused: Accused;
  banned_until?: string;
  reason?: string;
  note?: string;
  status_info: StatusInfo;
  created_at: string;
}

interface columnData {
  label: string;
  field: 'id' | 'category' | 'accused' | 'status_info' | 'created_at';
  transform?(
    value: string | Accused | Category | StatusInfo,
    row?: ReportData,
  ): string | React.ReactElement;
}

const Report: React.FC = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  const [showingFrom, setShowingFrom] = useState(0);
  const [showingTo, setShowingTo] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [data, setData] = useState<ReportData[]>([]);

  const [status, setStatus] = useState({
    rejected: false,
    pending: true,
    deferred: false,
  });

  const { rejected, pending, deferred } = status;

  const handleChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus({ ...status, [event.target.name]: event.target.checked });
  };

  const columns = useMemo<columnData[]>(
    () => [
      {
        label: 'Cadastro',
        field: 'created_at',
        transform: (created_at: string) =>
          formatDate(created_at, "dd/MM/yyyy 'às' HH:mm"),
      },
      {
        label: 'Usuário denunciado',
        field: 'accused',
        transform: (accused: Accused) => {
          const url =
            accused.role === 'student'
              ? `/alunos/visualizar/${accused.id}`
              : `/usuarios/visualizar/${accused.id}`;

          return <Link to={url}>{accused.show_name}</Link>;
        },
      },
      {
        label: 'Categoria',
        field: 'category',
        transform: (category: Category) => category.title,
      },
      {
        label: 'Status',
        field: 'status_info',
        transform: (status_info: StatusInfo) => (
          <Badge title={status_info.name} color={status_info.color} />
        ),
      },
      {
        label: '',
        field: 'id',
        transform: id => (
          <DropdownButton>
            <ul>
              <li>
                <Link to={`/denuncias/visualizar/${id}`}>
                  <MdKeyboardArrowRight size={18} />
                  Visualizar
                </Link>
              </li>
            </ul>
          </DropdownButton>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const response = await api.get('/reports', {
          params: { pageIndex, search, deferred, pending, rejected },
        });

        setMaxPages(response.data.maxPages);
        setShowingFrom(response.data.showingFrom);
        setShowingTo(response.data.showingTo);
        setTotalRecords(response.data.totalRecords);
        setData(response.data.records);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [pageIndex, search, deferred, pending, rejected]);

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs title="Denúncias" items={[{ title: 'Denúncias' }]}>
          <Button
            color="secondary"
            onClick={() => history.push('/denuncias/categorias')}
          >
            Categorias
          </Button>
        </Breadcrumbs>

        <Row>
          <Col md={12}>
            <Card>
              <FilterCard>
                <section>
                  <h2>Filtrar categoria</h2>
                </section>
                <section>
                  <h2>Filtrar usuário</h2>
                </section>
                <section>
                  <h2>Filtrar status</h2>

                  <CheckboxGroup>
                    <Checkbox
                      name="pending"
                      label="Pendentes"
                      color="warning"
                      checked={pending}
                      onChange={handleChangeStatus}
                    />

                    <Checkbox
                      name="rejected"
                      label="Arquivadas"
                      color="primary"
                      checked={rejected}
                      onChange={handleChangeStatus}
                    />

                    <Checkbox
                      name="deferred"
                      label="Punição aplicada"
                      color="danger"
                      checked={deferred}
                      onChange={handleChangeStatus}
                    />
                  </CheckboxGroup>
                </section>
              </FilterCard>
            </Card>

            <Card>
              <Datatable
                loading={loading}
                columns={columns}
                showingTo={showingTo}
                showingFrom={showingFrom}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                maxPages={maxPages}
                setPageIndex={(value: number) => setPageIndex(value)}
                setSearch={(value: string) => setSearch(value)}
              >
                {data.map(row => (
                  <tr key={row.id}>
                    {columns.map(column => (
                      <td key={column.field}>
                        {column.transform
                          ? column.transform(row[column.field], row)
                          : row[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
                {!data.length && (
                  <tr>
                    <td colSpan={columns.length}>Nenhum registro encontrado</td>
                  </tr>
                )}
              </Datatable>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Report;
